/**
 * The signal of `forgetBranchReads` (`branchReads.ts`): a branch of a project
 * was just created or deleted from this app. Its own module because both
 * `branchReads.ts` and `branchManagement.ts` (`useBranchMeta`) need it, and
 * `branchReads.ts` imports `branchManagement.ts`: a registry in either would
 * close an import cycle.
 */

import type { FetchApi } from '@backstage/frontend-plugin-api';

/**
 * Listeners of {@link onBranchReadsForgotten}, per FetchApi, as the other
 * registries (`onProjectApplications`). Never pruned beyond what
 * unsubscribing removes.
 */
const forgottenListeners = new WeakMap<
  FetchApi,
  Set<(project: string) => void>
>();

/**
 * Calls `listener` with the project of every `forgetBranchReads` made through
 * this FetchApi from now on. Returns the unsubscribe.
 */
export function onBranchReadsForgotten(
  fetchApi: FetchApi,
  listener: (project: string) => void,
): () => void {
  let listeners = forgottenListeners.get(fetchApi);
  if (!listeners) {
    listeners = new Set();
    forgottenListeners.set(fetchApi, listeners);
  }
  const subscribed = listeners;
  subscribed.add(listener);
  return () => {
    subscribed.delete(listener);
  };
}

/** Tells the listeners of {@link onBranchReadsForgotten}. */
export function announceBranchReadsForgotten(
  fetchApi: FetchApi,
  project: string,
): void {
  for (const listener of [...(forgottenListeners.get(fetchApi) ?? [])]) {
    // A listener that throws must not fail the dialog's success path.
    try {
      listener(project);
    } catch {
      // Its own business; the others are still told.
    }
  }
}
